import React, {useState} from "react";
import {useQueryClient} from "react-query";
import {useNavigate, useParams} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../Recoil/Atom/authState";
import {useGetQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {ResponseList, ResponseRemove} from "../../models/clientModels/interfaces/Response";

import * as revisionDocumentApi from "../../api/documentApi/revisionDocumentApi";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import {
  Box,
  Button,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from "@mui/material";
import ActionButtonGroup from "../../components/ActionButtonGroup";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {useDepartmentList} from "../../hooks/useDepartmentList";
import {documentActionButton} from "../../models/clientModels/data/actionButtonData";
import {
  EQRevisionDocument,
  initializeRevisionDocumentListParams,
  revisionTypeNames,
} from "../../models/serverModels/workOrder/EQRevisionDocument";
import {SubMenuGroup} from "../../styledComponents/StyledListContainer";
import {PaginationContainer} from "../../styledComponents/StyledPagination";
import {StyledNoneScrollTable, StyledNoneStickyTable} from "../../styledComponents/StyledTable";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1500px",
}));

const RevisionDocumentList = () => {
  const {id} = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const tokenState = useRecoilValue(tokenSelector);

  const setAlertState = useSetRecoilState(alertStateAtom);

  const [page, setPage] = useState<number>(1);

  const {departmentList} = useDepartmentList();

  const {
    data: revisionDocumentList,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<EQRevisionDocument>>({
    key: ["RevisionDocumentList", {id: parseInt(id!), token: tokenState, page}],
    api: () =>
      revisionDocumentApi.getList({
        ...initializeRevisionDocumentListParams,
        id: parseInt(id!),
        token: tokenState,
        page,
      }),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: revisionDocumentApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("RevisionDocumentList");
      },
    },
  });

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };
  return (
    <ListContainer>
      <SubTitle subTitle={revisionTypeNames[parseInt(id!)]} />
      <SubMenuGroup>
        <Box></Box>
        <ActionButtonGroup
          data={documentActionButton(() =>
            navigate(`/Document/CreateRevision`, {state: parseInt(id!)})
          )}
        />
      </SubMenuGroup>
      <StyledNoneScrollTable>
        <Table>
          <StyledNoneStickyTable>
            <TableRow>
              <TableCell align="center">작업명</TableCell>
              <TableCell align="center">작성부서</TableCell>
              <TableCell align="center">Part No</TableCell>
              <TableCell align="center">최초평가일</TableCell>
              <TableCell align="center">최근개정일</TableCell>
              <TableCell align="center">개정번호</TableCell>
              <TableCell align="center">비고</TableCell>
              <TableCell align="center">편집</TableCell>
            </TableRow>
          </StyledNoneStickyTable>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={8} />
            ) : (
              revisionDocumentList?.items.map((item) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">{item.name}</TableCell>
                  <TableCell align="center">
                    {" "}
                    {departmentList?.items?.map((value) =>
                      value.uid === item.departmentUID ? value?.name : ""
                    )}
                  </TableCell>
                  <TableCell align="center">{item.serialNo}</TableCell>
                  <TableCell align="center">
                    {new Date(item?.firstDate).getFullYear()}년
                    {new Date(item?.firstDate).getMonth() + 1}월{" "}
                    {new Date(item?.firstDate).getDate()}일
                  </TableCell>
                  <TableCell align="center">
                    {" "}
                    {new Date(item?.lastDate).getFullYear()}년
                    {new Date(item?.lastDate).getMonth() + 1}월 {new Date(item?.lastDate).getDate()}
                    일
                  </TableCell>
                  <TableCell align="center">{item.revisionNo}</TableCell>
                  <TableCell align="center">{item.extra}</TableCell>

                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      style={{marginRight: "12px"}}
                      onClick={() => {
                        navigate(`/Document/EditRevision/${item.uid}`, {
                          state: id,
                        });
                      }}
                    >
                      편집
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "등록된 서식을 삭제하시겠습니까?",
                          execution: () => removeMutate({token: tokenState, id: item.uid}),
                        });
                      }}
                    >
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledNoneScrollTable>
      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={revisionDocumentList?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default RevisionDocumentList;
